import React from 'react';
import { css } from '@emotion/react';
import { Controller, Control } from 'react-hook-form';
import {
  TextField,
  Checkbox,
  FormControlLabel,
  FormControl,
  Tooltip,
  Button,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpSharpIcon from '@mui/icons-material/HelpSharp';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import { ICreateResidentParams } from '../../../types/api/resident/createResident';
import { IGetUnitRes } from '../../../types/api/unit/getUnit';
import { MenuItemProps, Select } from '../../../components/mui/select';
import {
  BLADDER_THRESHOLD_PERCENTAGE,
  HOURS,
  INDEPENDENCE_LEVELS,
  SEX_JA,
  SEX_EN,
  SUPPORT_URL,
  SNOOZETERM_JA,
  SNOOZETERM_EN,
  SNOOZEPERIOD_JA,
  SNOOZEPERIOD_EN,
  GAS_STRENGTH,
  GAS_DURATION,
  DETAKAMO_LOGIC_JA,
  DETAKAMO_LOGIC_EN,
} from '../../../constants';
import { media } from '../../../styles/media';
import { color } from '../../../styles';
import { IGetDfreeDevicesRes } from '../../../types/api/dfree-devices/getDfreeDevices';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { IGetNursingHomeRes } from '../../../types/api/nursingHome/getNursingHome';
import { IGetDfreeBDevicesRes } from '../../../types/api/dfree-b-devices/getDfreeBDevices';
interface Props {
  isEdit: boolean;
  isDemoContract?: boolean;
  control: Control<ICreateResidentParams, object>;
  units: IGetUnitRes[];
  formSelectLists: {
    pants: { key: number | string; value: string }[];
    pads: { key: number | string; value: string }[];
    devices: IGetDfreeDevicesRes[];
    goodTreeList: { key: number | string; value: string }[];
    cdcList: { key: string; value: string }[];
    paramountList: { key: string; value: string }[];
    bDevices: IGetDfreeBDevicesRes[];
    careLevel: { key: number | string; value: string }[];
  };
  deviceId: string;
  bDeviceId: string;
  careSchedule: string[];
  isDisplayBigBladderOption?: boolean;
  isDisplayUrinationOption?: boolean;
  isDisplayGotUpOption?: boolean;
  lastNameError?: boolean;
  nameError?: boolean;
  unitError?: boolean;
  endDiaperMiddleHourError?: boolean;
  isAfternoonEndBeforeNightStart: (afternoonEnd: string | null) => boolean;
  isOnlyOneSelectedMiddle: (value: string | null, isBegin: boolean) => boolean;
  isOnlyOneSelectedNight: (value: string | null, isBegin: boolean) => boolean;
  onChangeCareSchedule: (editType: 'add' | 'change' | 'delete', schedule?: string, index?: number) => void;
  onChangeDeviceId: (id: string) => void;
  onChangeBDeviceId: (id: string) => void;
  onChangeSnoozeTerm: (type: string, value: string | number) => void;
  onChangeSnoozePeriod: (type: string, value: string | number) => void;
  onChangeDetakamoLogic: (value: string) => void;
  nursingHome: IGetNursingHomeRes;
  isEmptyFormData: Record<string, boolean>;
  hoursWithUnSelected: { key: string; value: string }[];
  localHoursWithUnSelected: { key: string; value: string }[];
  isNavigatedNotificationSettings?: boolean;
}

const Font14 = css`
  font-size: 14px;
`;

const Font16 = css`
  font-size: 16px;
`;

const MarginLeft = css`
  margin-left: 36px;
`;

const BottomSpacing = css`
  margin-bottom: 16px;
`;

const InnerBottomSpacing = css`
  padding-bottom: 8px;
`;

const BottomLine = css`
  border-bottom: 1px solid ${color.border_gray};
`;

const TopSpacing = css`
  margin-top: 16px;
`;

const ScrollMargin = css`
  scroll-margin-top: 112px;
`;

const WidthMax = css`
  width: 100%;
`;

const Container = css`
  padding: 40px 16px 0;

  ${media.minDesktop} {
    padding: 40px 100px 0;
  }
`;

const FormItem = css`
  display: flex;
`;

const AlignCenter = css`
  display: flex;
  align-items: center;
`;

const FlexBetween = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Label = css`
  flex: 0 0 18%;
  margin-right: 8px;
`;

const Required = css`
  color: #eb3223;
`;

const FormContent = css`
  width: 80%;
  flex: 1 1 80%;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const NotificationImage = css`
  margin-right: 8px;
  width: 24px;
  min-width: 24px;
  height: 24px;
`;

const NotificationLockContainer = css`
  padding-top: 16px;
  position: relative;
`;

const NotificationLockBody = css`
  pointer-events: none;
`;

const NotificationLockOverlay = css`
  position: absolute;
  inset: 0;
  background: #a3a3a399;
  z-index: 1;
`;


const SelectWrapper = css`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  column-gap: 16px;
`;

const CheckboxSelectContainer = css`
  padding: 8px 16px;
  background: ${color.light_green};
  margin-right: 8px;
  border-radius: 8px;
  ${BottomSpacing};
`;

const CareTimingContainer = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 16px;
  background: ${color.white};
  margin-bottom: 8px;
`;

const GotUpContainer = css`
  display: flex;
  align-items: center;
  justify-content: start;
  flex-direction: column;
  ${BottomSpacing};
`;

const CareScheduleContainer = css`
  ${AlignCenter};
  min-width: 180px;
  gap: 8px;
`;

const ScheduleSelector = css`
  min-width: 88px;
`;

const AddTimingButtonStyle = css`
  margin-top: 16px;
  display: flex;
  align-items: center;
`;

const BarTitle = css`
  padding: 4px 15px;
  border-left: 5px solid;
  font-weight: bold;
  ${BottomSpacing};
`;

const InlineHelp = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const ContingencyModeSub = css`
  color: ${color.dark_gray};
  font-size: 14px;
`;

const ContingencyModeText = css`
  padding: 12px 16px;
  background: ${color.light_green};
  border-radius: 8px;
  font-size: 16px;
`;

const DemoModeText = css`
  padding: 0 16px 16px;
  color: ${color.red};
  font-size: 14px;
`;

export const ResidentSettingForm = React.memo(
  ({
    isEdit,
    isDemoContract = false,
    control,
    units,
    formSelectLists,
    deviceId,
    bDeviceId,
    careSchedule,
    isDisplayBigBladderOption = false,
    isDisplayUrinationOption = false,
    isDisplayGotUpOption = false,
    nameError = false,
    lastNameError = false,
    unitError = false,
    endDiaperMiddleHourError = false,
    isAfternoonEndBeforeNightStart,
    isOnlyOneSelectedMiddle,
    isOnlyOneSelectedNight,
    onChangeCareSchedule,
    onChangeDeviceId,
    onChangeBDeviceId,
    onChangeSnoozeTerm,
    onChangeSnoozePeriod,
    onChangeDetakamoLogic,
    isEmptyFormData,
    nursingHome,
    hoursWithUnSelected,
    localHoursWithUnSelected,
    isNavigatedNotificationSettings = false,
  }: Props) => {
    const { showSnackbar } = useSnackbar();
    const { t, i18n } = useTranslation();
    const defaultTheme = useTheme();
    const [isNotificationAccordionExpanded, setIsNotificationAccordionExpanded] = React.useState(false);
    const tooltipTheme = React.useMemo(
      () =>
        createTheme({
          ...defaultTheme,
          components: {
            MuiTooltip: {
              defaultProps: { arrow: true },
              styleOverrides: {
                tooltip: {
                  maxWidth: 560,
                  fontSize: '14px',
                  padding: '8px',
                },
                arrow: { color: color.dark_gray },
              },
            },
          },
        }),
      [],
    );

    const sexOptions = React.useMemo<MenuItemProps[]>(() => {
      return i18n.language.startsWith('ja') ? SEX_JA : SEX_EN;
    }, [i18n.language]);

    const memoUnits = React.useMemo(() => {
      return units.map((unit) => {
        return { key: unit.id, value: unit.name };
      });
    }, []);

    const enableAddTimingButton = React.useMemo(() => {
      return !careSchedule.some((v) => !v);
    }, [careSchedule]);

    const selectableTimingList: MenuItemProps[] = React.useMemo(() => {
      return localHoursWithUnSelected.map((v, i) => ({
        ...v,
        disabled: i !== 0 && careSchedule.includes(v.key),
      }));
    }, [careSchedule]);

    const memoDevices = React.useMemo(() => {
      const devices = formSelectLists.devices.map((device) => {
        // TODO: 英語の場合は逆にする
        let attachedName = '';
        if (device.lastName || device.firstName) {
          const lastName = device.lastName ? device.lastName : '';
          const firstName = device.firstName ? device.firstName : '';
          if (i18n.language.startsWith('ja')) {
            attachedName = ' (' + lastName + ' ' + firstName + 'さん 利用中)';
          } else {
            attachedName = ' (' + firstName + ' ' + lastName + ' In use)';
          }
        }
        return { key: String(device.id), value: device.serialNumber + attachedName };
      });
      devices.unshift({ key: '', value: t('unregisterDFree', 'DFree登録を解除') });
      return devices;
    }, []);

    const memoBDevices = React.useMemo(() => {
      const devices = formSelectLists.bDevices.map((device) => {
        // TODO: 英語の場合は逆にする
        let attachedName = '';
        if (device.lastName || device.firstName) {
          const lastName = device.lastName ? device.lastName : '';
          const firstName = device.firstName ? device.firstName : '';
          if (i18n.language.startsWith('ja')) {
            attachedName = ' (' + lastName + ' ' + firstName + 'さん 利用中)';
          } else {
            attachedName = ' (' + firstName + ' ' + lastName + ' In use)';
          }
        }
        return { key: String(device.id), value: device.serialNumber + attachedName };
      });
      devices.unshift({ key: '', value: t('unregisterDFree', 'DFree登録を解除') });
      return devices;
    }, []);

    return (
      <ThemeProvider theme={tooltipTheme}>
        <div css={Container}>
          <div css={BarTitle}>{t('editResidentInformation', '入居者情報編集')}</div>
          <div css={FormItem}>
            <label css={[Label, TopSpacing]}>
              {t('lastName', '姓')}
              <span css={Required}>*</span>
            </label>
            <div css={FormContent}>
              <Controller
                control={control}
                name='lastName'
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextField
                    sx={{ width: '100%' }}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    label={t('lastName', '姓')}
                    error={lastNameError}
                    helperText={
                      lastNameError &&
                      t('lastName', '姓') +
                        (i18n.language.startsWith('ja') ? '' : ' ') +
                        t('isRequired', 'は必須入力です。')
                    }
                  />
                )}
              />
            </div>
          </div>
          <div css={FormItem}>
            <label css={[Label, TopSpacing]}>
              {t('firstName', '名')}
              <span css={Required}>*</span>
            </label>
            <div css={FormContent}>
              <Controller
                control={control}
                name='name'
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextField
                    sx={{ width: '100%' }}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    error={nameError}
                    label={t('firstName', '名')}
                    helperText={
                      nameError &&
                      t('firstName', '名') +
                        (i18n.language.startsWith('ja') ? '' : ' ') +
                        t('isRequired', 'は必須入力です。')
                    }
                  />
                )}
              />
            </div>
          </div>
          {isEdit && nursingHome.goodTree && ( // 編集画面かつケア樹連携フラグが立っている場合のみ表示する
            <div css={FormItem}>
              <div css={[Label, TopSpacing]} />
              <div css={FormContent}>
                <Controller
                  control={control}
                  name='goodTreeResidentId'
                  render={({ field: { value, onChange } }) => (
                    <div css={CheckboxSelectContainer}>
                      <div css={FlexBetween}>
                        <div>ケア樹連携先入居者</div>
                        <Select
                          style={{ width: '60%' }}
                          value={value ?? formSelectLists.goodTreeList[0].key}
                          listData={formSelectLists.goodTreeList}
                          isDisplayEmpty
                          isEmptyValue={isEmptyFormData.goodTreeResidentId}
                          onChange={(v) => {
                            if (v.key == '-') {
                              onChange(v.key);
                            } else {
                              onChange(+v.key);
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}
                />
              </div>
            </div>
          )}
          {isEdit &&
            nursingHome.cdc && ( // ケアデータコネクト連携フラグが立っている場合のみ表示する
              <div css={FormItem}>
                <div css={[Label]} />
                <div css={FormContent}>
                  <div css={CheckboxSelectContainer}>
                    <div css={FlexBetween}>
                      <div>ケアデータコネクト連携先入居者</div>
                      <Controller
                        control={control}
                        name='careDataConnectResidentId'
                        render={({ field: { value, onChange } }) => (
                          <Select
                            style={{ width: '60%' }}
                            value={value ?? formSelectLists.cdcList[0]}
                            listData={formSelectLists.cdcList}
                            isDisplayEmpty
                            isEmptyValue={isEmptyFormData.careDataConnectResidentId}
                            onChange={(v) => {
                              onChange(v.key);
                            }}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

          {isEdit &&
            nursingHome.inUseParamount && ( // パラマウント連携フラグが立っている場合のみ表示する
              <div css={FormItem}>
                <div css={[Label]} />
                <div css={FormContent}>
                  <div css={CheckboxSelectContainer}>
                    <div css={FlexBetween}>
                      <div>パラマウント連携先入居者</div>
                      <Controller
                        control={control}
                        name='paramountResidentId'
                        render={({ field: { value, onChange } }) => (
                          <Select
                            style={{ width: '60%' }}
                            value={value ?? formSelectLists.paramountList[0]}
                            listData={formSelectLists.paramountList}
                            isDisplayEmpty
                            isEmptyValue={isEmptyFormData.paramountResidentId}
                            onChange={(v) => {
                              onChange(v.key);
                            }}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          <div css={FormItem}>
            <label css={[Label, TopSpacing]}>
              {t('unitName', 'ユニット名')}
              <span css={Required}>*</span>
            </label>
            <div css={FormContent}>
              <Controller
                control={control}
                name='nursingHomeUnitId'
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    style={{ width: '100%' }}
                    value={value ? String(value) : null}
                    listData={memoUnits}
                    label={t('unitName', 'ユニット名')}
                    onChange={(v) => {
                      onChange(v.key);
                    }}
                    isError={unitError}
                    errorText={
                      unitError
                        ? t('unitName', 'ユニット名') +
                          (i18n.language.startsWith('ja') ? '' : ' ') +
                          t('isRequired', 'は必須入力です。')
                        : ''
                    }
                  />
                )}
              />
            </div>
          </div>
          <Accordion disableGutters sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
            <AccordionSummary css={BottomLine} expandIcon={<ExpandMoreIcon />} sx={{ px: 0, mb: 2 }}>
              <div>{t('other', 'その他')}</div>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0 }}>
              <div css={FormItem}>
                <label css={[Label, TopSpacing]}>{t('roomName', '部屋名')}</label>
                <div css={FormContent}>
                  <Controller
                    control={control}
                    name='roomName'
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        sx={{ width: '100%' }}
                        label={t('roomName', '部屋名')}
                        onChange={onChange}
                        value={value}
                      />
                    )}
                  />
                </div>
              </div>
              <div css={FormItem}>
                <label css={[Label, TopSpacing]}>{t('bed', 'ベッド番号')}</label>
                <div css={FormContent}>
                  <Controller
                    control={control}
                    name='bed'
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        sx={{ width: '100%' }}
                        label={t('bed', 'ベッド番号')}
                        onChange={onChange}
                        value={value}
                      />
                    )}
                  />
                </div>
              </div>
              <div css={FormItem}>
                <label css={[Label, TopSpacing]}>{t('gender', '性別')}</label>
                <div css={FormContent}>
                  <Controller
                    control={control}
                    name='sex'
                    render={({ field: { onChange, value } }) => (
                      <Select
                        label={t('gender', '性別')}
                        listData={sexOptions}
                        value={value ?? null}
                        isDisplayEmpty
                        isEmptyValue={!value}
                        fullWidth
                        onChange={(v) => onChange(v.key === '-' ? null : v.key)}
                      />
                    )}
                  />
                </div>
              </div>
              {i18n.language.startsWith('ja') && (
                <>
                  <div css={FormItem}>
                    <label css={[Label, TopSpacing]}>排泄自立度：</label>
                    <div css={FormContent}>
                      <Controller
                        control={control}
                        name='careSettings.independenceLevel'
                        render={({ field: { value, onChange } }) => (
                          <Select
                            style={{ width: '100%' }}
                            label='排泄自立度'
                            value={value}
                            listData={INDEPENDENCE_LEVELS}
                            isEmptyValue={isEmptyFormData.independenceLevel}
                            onChange={(v) => {
                              onChange(v.key);
                            }}
                          />
                        )}
                      />
                    </div>
                  </div>
                  <div css={FormItem}>
                    <label css={[Label, TopSpacing]}>要介護度</label>
                    <div css={FormContent}>
                      <Controller
                        control={control}
                        name='careSettings.careLevel'
                        render={({ field: { value, onChange } }) => (
                          <Select
                            style={{ width: '100%', marginBottom: '8px' }}
                            label='要介護度'
                            value={value}
                            listData={formSelectLists.careLevel}
                            isEmptyValue={isEmptyFormData.careLevel}
                            onChange={(v) => {
                              onChange(v.key);
                            }}
                          />
                        )}
                      />
                    </div>
                  </div>
                </>
              )}
              {i18n.language.startsWith('ja') && (
                <>
                  <div css={[Label, BottomSpacing]}>排泄消耗品</div>
                  <div css={FormItem}>
                    <label css={[Label, TopSpacing]}>昼用1</label>
                    <div css={FormContent}>
                      <div css={SelectWrapper}>
                        <img
                          src='/images/residents/residentSetting/pants.svg'
                          alt='テープ/パンツ'
                          width='23'
                          height='20'
                        />
                        <Controller
                          control={control}
                          name='careSettings.nursingHomeDiaperIdNoon'
                          render={({ field: { onChange, value } }) => (
                            <Select
                              style={{ width: '100%' }}
                              label='テープ/パンツ'
                              value={value}
                              listData={formSelectLists.pants}
                              isEmptyValue={isEmptyFormData.nursingHomeDiaperIdNoon}
                              onChange={(v) => {
                                onChange(v.key);
                              }}
                            />
                          )}
                        />
                      </div>
                      <div css={SelectWrapper}>
                        <img src='/images/residents/residentSetting/pad.svg' alt='パッド' width='20' height='27' />
                        <Controller
                          control={control}
                          name='careSettings.nursingHomePadIdNoonFirst'
                          render={({ field: { value, onChange } }) => (
                            <Select
                              style={{ width: '100%' }}
                              label='パッド1枚目'
                              value={value}
                              listData={formSelectLists.pads}
                              isEmptyValue={isEmptyFormData.nursingHomePadIdNoonFirst}
                              onChange={(v) => {
                                onChange(v.key);
                              }}
                            />
                          )}
                        />
                      </div>
                      <div css={SelectWrapper}>
                        <img src='/images/residents/residentSetting/pad.svg' alt='パッド' width='20' height='27' />
                        <Controller
                          control={control}
                          name='careSettings.nursingHomePadIdNoonSecond'
                          render={({ field: { value, onChange } }) => (
                            <Select
                              style={{ width: '100%' }}
                              label='パッド2枚目'
                              value={value}
                              listData={formSelectLists.pads}
                              isEmptyValue={isEmptyFormData.nursingHomePadIdNoonSecond}
                              onChange={(v) => {
                                onChange(v.key);
                              }}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  <div css={FormItem}>
                    <label css={[Label, TopSpacing]}>昼用2</label>
                    <div css={FormContent}>
                      <div>
                        <div css={[CheckboxSelectContainer, MarginLeft]}>
                          <div css={FlexBetween}>
                            <div>使用時間帯</div>
                            <div css={AlignCenter}>
                              <Controller
                                control={control}
                                name='careSettings.beginDiaperMiddleHour'
                                rules={{
                                  validate: (value) => {
                                    if (isOnlyOneSelectedMiddle(value, true)) {
                                      showSnackbar('使用時間帯は開始・終了を両方選択してください', 'error');
                                      return false;
                                    }
                                    return true;
                                  },
                                }}
                                render={({ field: { onChange, value } }) => (
                                  <Select
                                    listData={hoursWithUnSelected}
                                    value={value ?? hoursWithUnSelected[0].key}
                                    isDisplayEmpty
                                    isEmptyValue={isEmptyFormData.beginDiaperMiddleHour}
                                    onChange={(v) => onChange(v.key)}
                                  />
                                )}
                              />
                              -
                              <Controller
                                control={control}
                                name='careSettings.endDiaperMiddleHour'
                                rules={{
                                  validate: (value) => {
                                    if (isOnlyOneSelectedMiddle(value, false)) {
                                      showSnackbar('使用時間帯は開始・終了を両方選択してください', 'error');
                                      return false;
                                    }
                                    if (isAfternoonEndBeforeNightStart(value)) {
                                      showSnackbar('夜用の開始時刻より前に設定してください', 'error');
                                      return false;
                                    }
                                    return true;
                                  },
                                }}
                                render={({ field: { onChange, value } }) => (
                                  <Select
                                    listData={hoursWithUnSelected}
                                    value={value ?? hoursWithUnSelected[0].key}
                                    isDisplayEmpty
                                    isEmptyValue={isEmptyFormData.endDiaperMiddleHour}
                                    onChange={(v) => onChange(v.key)}
                                    isError={endDiaperMiddleHourError}
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div css={SelectWrapper}>
                        <img
                          src='/images/residents/residentSetting/pants.svg'
                          alt='テープ/パンツ'
                          width='23'
                          height='20'
                        />
                        <Controller
                          control={control}
                          name='careSettings.nursingHomeDiaperIdMiddle'
                          render={({ field: { onChange, value } }) => (
                            <Select
                              style={{ width: '100%' }}
                              label='テープ/パンツ'
                              value={value}
                              listData={formSelectLists.pants}
                              isEmptyValue={isEmptyFormData.nursingHomeDiaperIdMiddle}
                              onChange={(v) => {
                                onChange(v.key);
                              }}
                            />
                          )}
                        />
                      </div>
                      <div css={SelectWrapper}>
                        <img src='/images/residents/residentSetting/pad.svg' alt='パッド' width='20' height='27' />
                        <Controller
                          control={control}
                          name='careSettings.nursingHomePadIdMiddleFirst'
                          render={({ field: { onChange, value } }) => (
                            <Select
                              style={{ width: '100%' }}
                              label='パッド1枚目'
                              value={value}
                              listData={formSelectLists.pads}
                              isEmptyValue={isEmptyFormData.nursingHomePadIdMiddleFirst}
                              onChange={(v) => {
                                onChange(v.key);
                              }}
                            />
                          )}
                        />
                      </div>
                      <div css={SelectWrapper}>
                        <img src='/images/residents/residentSetting/pad.svg' alt='パッド' width='20' height='27' />
                        <Controller
                          control={control}
                          name='careSettings.nursingHomePadIdMiddleSecond'
                          render={({ field: { onChange, value } }) => (
                            <Select
                              style={{ width: '100%' }}
                              label='パッド2枚目'
                              value={value}
                              listData={formSelectLists.pads}
                              isEmptyValue={isEmptyFormData.nursingHomePadIdMiddleSecond}
                              onChange={(v) => {
                                onChange(v.key);
                              }}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  <div css={FormItem}>
                    <label css={[Label, TopSpacing]}>夜用</label>
                    <div css={FormContent}>
                      <div css={[CheckboxSelectContainer, MarginLeft]}>
                        <div css={FlexBetween}>
                          <div>使用時間帯</div>
                          <div css={AlignCenter}>
                            <Controller
                              control={control}
                              name='careSettings.beginDiaperNightHour'
                              rules={{
                                validate: (value) => {
                                  if (isOnlyOneSelectedNight(value, true)) {
                                    showSnackbar('使用時間帯は開始・終了を両方選択してください', 'error');
                                    return false;
                                  }
                                  return true;
                                },
                              }}
                              render={({ field: { onChange, value } }) => (
                                <Select
                                  listData={hoursWithUnSelected}
                                  value={value ?? hoursWithUnSelected[0].key}
                                  isDisplayEmpty
                                  isEmptyValue={isEmptyFormData.beginDiaperNightHour}
                                  onChange={(v) => onChange(v.key)}
                                />
                              )}
                            />
                            -
                            <Controller
                              control={control}
                              name='careSettings.endDiaperNightHour'
                              rules={{
                                validate: (value) => {
                                  if (isOnlyOneSelectedNight(value, false)) {
                                    showSnackbar('使用時間帯は開始・終了を両方選択してください', 'error');
                                    return false;
                                  }
                                  return true;
                                },
                              }}
                              render={({ field: { onChange, value } }) => (
                                <Select
                                  listData={hoursWithUnSelected}
                                  value={value ?? hoursWithUnSelected[0].key}
                                  isDisplayEmpty
                                  isEmptyValue={isEmptyFormData.endDiaperNightHour}
                                  onChange={(v) => onChange(v.key)}
                                />
                              )}
                            />
                          </div>
                        </div>
                      </div>
                      <div css={SelectWrapper}>
                        <img
                          src='/images/residents/residentSetting/pants.svg'
                          alt='テープ/パンツ'
                          width='23'
                          height='20'
                        />
                        <Controller
                          control={control}
                          name='careSettings.nursingHomeDiaperIdNight'
                          render={({ field: { value, onChange } }) => (
                            <Select
                              style={{ width: '100%' }}
                              label='テープ/パンツ'
                              value={value}
                              listData={formSelectLists.pants}
                              isEmptyValue={isEmptyFormData.nursingHomeDiaperIdNight}
                              onChange={(v) => {
                                onChange(v.key);
                              }}
                            />
                          )}
                        />
                      </div>
                      <div css={SelectWrapper}>
                        <img src='/images/residents/residentSetting/pad.svg' alt='パッド' width='20' height='27' />
                        <Controller
                          control={control}
                          name='careSettings.nursingHomePadIdNightFirst'
                          render={({ field: { value, onChange } }) => (
                            <Select
                              style={{ width: '100%' }}
                              label='パッド1枚目'
                              value={value}
                              listData={formSelectLists.pads}
                              isEmptyValue={isEmptyFormData.nursingHomePadIdNightFirst}
                              onChange={(v) => {
                                onChange(v.key);
                              }}
                            />
                          )}
                        />
                      </div>
                      <div css={SelectWrapper}>
                        <img src='/images/residents/residentSetting/pad.svg' alt='パッド' width='20' height='27' />
                        <Controller
                          control={control}
                          name='careSettings.nursingHomePadIdNightSecond'
                          render={({ field: { value, onChange } }) => (
                            <Select
                              style={{ width: '100%' }}
                              label='パッド2枚目'
                              value={value}
                              listData={formSelectLists.pads}
                              isEmptyValue={isEmptyFormData.nursingHomePadIdNightSecond}
                              onChange={(v) => {
                                onChange(v.key);
                              }}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </AccordionDetails>
          </Accordion>
          <div css={BarTitle}>{t('dFreeSettings', 'DFree設定')}</div>
          <div css={FormItem}>
            <label css={[Label, TopSpacing]}>{t('dfreeSn', 'DFree SN')}</label>
            <div css={FormContent}>
              <FormControl sx={{ width: '100%' }}>
                <Select
                  label={t('dfreeSn', 'DFree SN')}
                  listData={memoDevices}
                  value={deviceId ?? ''}
                  onChange={(v) => onChangeDeviceId(v.key as string)}
                />
              </FormControl>
            </div>
          </div>
          <Accordion
            disableGutters
            expanded={isNavigatedNotificationSettings || isNotificationAccordionExpanded}
            onChange={(_, expanded) => setIsNotificationAccordionExpanded(expanded)}
            sx={{ boxShadow: 'none', '&:before': { display: 'none' }, mt: 2 }}
          >
            <AccordionSummary css={BottomLine} expandIcon={<ExpandMoreIcon />} sx={{ px: 0, mb: 2 }}>
              <div id='notification-settings' css={ScrollMargin}>
                {t('notificationSettings', '通知設定')}
              </div>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {isDemoContract && (
                <div css={DemoModeText}>
                  {
                    '現在トライアル期間中のため、通知の確認でお手間を取らせないよう、一部の通知機能を制限させていただいております。もし「すべての通知を受け取りたい」とご要望がございましたら、いつでもお気軽にサポート担当までお申し付けください。'
                  }
                </div>
              )}
              <div css={[FormItem, NotificationLockContainer]}>
                {isDemoContract && <div css={NotificationLockOverlay} />}
                <label css={Label} />
                <div css={FormContent}>
                  <div css={isDemoContract ? NotificationLockBody : undefined}>
                    <Controller
                      control={control}
                      name='bigBladderNotification'
                      render={({ field: { onChange, value } }) => (
                        <FormControlLabel
                          sx={{ marginBottom: '8px' }}
                          label={t('receiveAlmostNotification', 'そろそろ通知を受け取る')}
                          control={
                            <>
                              <Checkbox
                                sx={{ padding: 0, marginRight: '8px' }}
                                disabled={isDemoContract}
                                checked={value}
                                onChange={(v) => onChange(v.target.checked)}
                              />
                              <img css={NotificationImage} src={`/images/notification/icon_bigbladder.svg`} />
                            </>
                          }
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name='bladderThresholdPercentage'
                      render={({ field: { onChange, value } }) => (
                        <div css={CheckboxSelectContainer}>
                          <div css={FlexBetween}>
                            <div>{t('almostThreshold', 'そろそろライン')}</div>
                            <Select
                              listData={BLADDER_THRESHOLD_PERCENTAGE}
                              value={String(value)}
                              disabled={isDemoContract}
                              onChange={(v) => onChange(v.key)}
                            />
                          </div>
                        </div>
                      )}
                    />
                    {isDisplayBigBladderOption && (
                      <>
                        <Controller
                          control={control}
                          name='bigBladderSnoozeTerm'
                          render={({ field: { onChange, value } }) => (
                            <div css={CheckboxSelectContainer}>
                              <div css={FlexBetween}>
                                <div>{t('SnoozeInterval', '繰り返し間隔')}</div>
                                <Select
                                  listData={i18n.language.startsWith('ja') ? SNOOZETERM_JA : SNOOZETERM_EN}
                                  value={String(value)}
                                  disabled={isDemoContract}
                                  onChange={(v) => {
                                    onChange(+v.key);
                                    onChangeSnoozeTerm('bigBladder', v.key);
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        />
                        <Controller
                          control={control}
                          name='bigBladderSnoozePeriod'
                          render={({ field: { onChange, value } }) => (
                            <div css={CheckboxSelectContainer}>
                              <div css={FlexBetween}>
                                <div>{t('SnoozeEnd', '繰り返し終了')}</div>
                                <Select
                                  listData={i18n.language.startsWith('ja') ? SNOOZEPERIOD_JA : SNOOZEPERIOD_EN}
                                  value={String(value)}
                                  disabled={isDemoContract}
                                  onChange={(v) => {
                                    onChange(+v.key);
                                    onChangeSnoozePeriod('bigBladder', v.key);
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        />
                      </>
                    )}
                    <Controller
                      control={control}
                      name='urinationNotification'
                      render={({ field: { onChange, value } }) => (
                        <div>
                          <FormControlLabel
                            sx={{ marginBottom: '8px' }}
                            label={
                              <span css={InlineHelp}>
                                {t('receiveCheckNotification', 'でたかも通知を受け取る')}
                                <Tooltip
                                  title={t(
                                    'dfreeExplanationForUrinationNotification',
                                    'DFreeは、膀胱の尿のたまり具合を見ている機器です。ご利用者さまの動きや姿勢により、膀胱が見えなくなった場合にも通知されることがあるため、ベッド上で過ごされる方への使用を推奨しております。トイレへ行くことができる方には、「そろそろ通知」のご使用をおすすめいたします。',
                                  )}
                                  placement='top'
                                  arrow
                                >
                                  <HelpSharpIcon fontSize='small' sx={{ color: color.dark_gray }} />
                                </Tooltip>
                              </span>
                            }
                            control={
                              <>
                                <Checkbox
                                  sx={{ padding: 0, marginRight: '8px' }}
                                  disabled={isDemoContract}
                                  checked={value}
                                  onChange={(v) => onChange(v.target.checked)}
                                />
                                <img css={NotificationImage} src={`/images/notification/icon_urinated.svg`} />
                              </>
                            }
                          />
                        </div>
                      )}
                    />
                    {isDisplayUrinationOption && (
                      <>
                        <Controller
                          control={control}
                          name='urinationSnoozeTerm'
                          render={({ field: { onChange, value } }) => (
                            <div css={CheckboxSelectContainer}>
                              <div css={FlexBetween}>
                                <div>{t('SnoozeInterval', '繰り返し間隔')}</div>
                                <Select
                                  listData={i18n.language.startsWith('ja') ? SNOOZETERM_JA : SNOOZETERM_EN}
                                  value={String(value)}
                                  disabled={isDemoContract}
                                  onChange={(v) => {
                                    onChange(+v.key);
                                    onChangeSnoozeTerm('urination', v.key);
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        />
                        <Controller
                          control={control}
                          name='urinationSnoozePeriod'
                          render={({ field: { onChange, value } }) => (
                            <div css={CheckboxSelectContainer}>
                              <div css={FlexBetween}>
                                <div>{t('SnoozeEnd', '繰り返し終了')}</div>
                                <Select
                                  listData={i18n.language.startsWith('ja') ? SNOOZEPERIOD_JA : SNOOZEPERIOD_EN}
                                  value={String(value)}
                                  disabled={isDemoContract}
                                  onChange={(v) => {
                                    onChange(+v.key);
                                    onChangeSnoozePeriod('urination', v.key);
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        />
                      </>
                    )}
                    <Controller
                      control={control}
                      name='gotUpNotification'
                      render={({ field: { onChange, value } }) => (
                        <div>
                          <FormControlLabel
                            sx={{ marginBottom: '8px' }}
                            label={t('receiveGettingUpNotification', '起き上がり通知を受け取る')}
                            control={
                              <>
                                <Checkbox
                                  sx={{ padding: 0, marginRight: '8px' }}
                                  disabled={isDemoContract}
                                  checked={value}
                                  onChange={(v) => onChange(v.target.checked)}
                                />
                                <img css={NotificationImage} src={`/images/notification/icon_gotUp.svg`} />
                              </>
                            }
                          />
                        </div>
                      )}
                    />
                    {isDisplayGotUpOption && (
                      <>
                        <div css={[CheckboxSelectContainer, GotUpContainer]}>
                          <div css={[WidthMax, FlexBetween]}>
                            <div>{t('notificationTimeZone', '通知時間帯')}</div>
                            <div css={AlignCenter}>
                              <Controller
                                control={control}
                                name='beginGotUpCheckTerm'
                                render={({ field: { onChange, value } }) => (
                                  <Select
                                    listData={HOURS}
                                    value={value}
                                    disabled={isDemoContract}
                                    onChange={(v) => onChange(v.key)}
                                  />
                                )}
                              />
                              -
                              <Controller
                                control={control}
                                name='endGotUpCheckTerm'
                                render={({ field: { onChange, value } }) => (
                                  <Select
                                    listData={HOURS}
                                    value={value}
                                    disabled={isDemoContract}
                                    onChange={(v) => onChange(v.key)}
                                  />
                                )}
                              />
                            </div>
                          </div>
                          <div css={[WidthMax, ContingencyModeSub]}>
                            {t(
                              'NotificationLoopSettingDescription',
                              '※起き上がり通知を24時間有効にしたい場合は、上欄の開始時刻と終了時刻を同じ時刻に設定してください。例）19:00 - 19:00',
                            )}
                          </div>
                        </div>
                        <Controller
                          control={control}
                          name='gotUpSnoozeTerm'
                          render={({ field: { onChange, value } }) => (
                            <div css={[CheckboxSelectContainer, GotUpContainer]}>
                              <div css={[WidthMax, FlexBetween]}>
                                <div>{t('SnoozeInterval', '繰り返し間隔')}</div>
                                <Select
                                  listData={i18n.language.startsWith('ja') ? SNOOZETERM_JA : SNOOZETERM_EN}
                                  value={String(value)}
                                  disabled={isDemoContract}
                                  onChange={(v) => {
                                    onChange(+v.key);
                                    onChangeSnoozeTerm('gotUp', v.key);
                                  }}
                                />
                              </div>
                              <div css={[WidthMax, ContingencyModeSub]}>
                                ※{t('NotificationIntervalMayVaryBySeveralMinutes', '間隔が数分前後することがあります')}
                              </div>
                            </div>
                          )}
                        />
                        <Controller
                          control={control}
                          name='gotUpSnoozePeriod'
                          render={({ field: { onChange, value } }) => (
                            <div css={CheckboxSelectContainer}>
                              <div css={FlexBetween}>
                                <div>{t('SnoozeEnd', '繰り返し終了')}</div>
                                <Select
                                  listData={i18n.language.startsWith('ja') ? SNOOZEPERIOD_JA : SNOOZEPERIOD_EN}
                                  value={String(value)}
                                  disabled={isDemoContract}
                                  onChange={(v) => {
                                    onChange(+v.key);
                                    onChangeSnoozePeriod('gotUp', v.key);
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        />
                      </>
                    )}
                    <Controller
                      control={control}
                      name='zeroResetNotification'
                      render={({ field: { onChange, value } }) => (
                        <FormControlLabel
                          sx={{ marginBottom: '8px' }}
                          label={t('notificationIfSensorIsNotAttachedProperly', '装着開始時エラー通知を受け取る')}
                          control={
                            <>
                              <Checkbox
                                sx={{ padding: 0, marginRight: '8px' }}
                                disabled={isDemoContract}
                                checked={value}
                                onChange={(v) => onChange(v.target.checked)}
                              />
                              <img css={NotificationImage} src={`/images/notification/icon_zeroReset.svg`} />
                            </>
                          }
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </AccordionDetails>
          </Accordion>
          <div id='advanced-settings' css={[ScrollMargin, BottomLine, TopSpacing, InnerBottomSpacing, BottomSpacing]}>
            {t('advancedSettings', '高度な設定')}
          </div>
          <div css={FormItem}>
            <label css={Label} />
            <div css={FormContent}>
              {/* medicalの英語版では活動レベル設定を表示しているが、Professionalの英語版では非表示 */}
              {i18n.language.startsWith('ja') && (
                <>
                  <div css={[FlexBetween, BottomSpacing]}>
                    <div css={[Font16, InlineHelp]}>
                      {t('activityLevelSettings', '活動レベル設定')}
                      <Tooltip
                        title={
                          <span>
                            {t(
                              'dependingOnYourActivityPostureAndDeviceAttachmentTheDeviceMayFalselyDetectADecreaseInTheLevelOfUrinaryRetention',
                              '活動や姿勢、装着状況により尿のたまり具合の減少を誤検知する場合があります。',
                            )}
                            <br />
                            {t(
                              'selectingForPeopleWithLimitedMobilityMayReduceFalseDetections',
                              '「動きが少ない人用」を選択することで、誤検知を少なくできる場合があります。',
                            )}
                            <br />
                            {t('urinationRecordsAreNotTracked.', '※排尿記録はつきません')}
                          </span>
                        }
                        placement='top'
                        arrow
                      >
                        <HelpSharpIcon fontSize='small' sx={{ color: color.dark_gray }} />
                      </Tooltip>
                    </div>
                    <Controller
                      control={control}
                      name='detakamoLogic'
                      render={({ field: { onChange, value } }) => (
                        <>
                          <Select
                            listData={i18n.language.startsWith('ja') ? DETAKAMO_LOGIC_JA : DETAKAMO_LOGIC_EN}
                            value={value}
                            onChange={(v) => {
                              onChange(v.key);
                              onChangeDetakamoLogic(String(v.key));
                            }}
                          />
                        </>
                      )}
                    />
                  </div>
                </>
              )}
              <div css={BottomSpacing}>
                <Controller
                  control={control}
                  name='contingencyMode'
                  render={({ field: { onChange, value } }) => (
                    <FormControlLabel
                      label={
                        <span css={InlineHelp}>
                          {t('rescueMode', '救済モード')}
                          <Tooltip
                            title={
                              <span>
                                {t(
                                  'pleaseTryThisModeIfYouHaveALotOfLowerAbdominalFatOrIfTheLevelOfUrinaryRetentionDoesNotIncreaseAboveLevel4',
                                  '下腹部の脂肪が厚い方への使用など、尿のたまり具合が4よりあがらない場合にお試しください。',
                                )}
                              </span>
                            }
                            placement='top'
                            arrow
                          >
                            <HelpSharpIcon fontSize='small' sx={{ color: color.dark_gray }} />
                          </Tooltip>
                        </span>
                      }
                      control={
                        <Checkbox
                          sx={{ padding: 0, marginRight: '8px' }}
                          checked={value}
                          onChange={(v) => onChange(v.target.checked)}
                        />
                      }
                    />
                  )}
                />
              </div>
              {i18n.language.startsWith('ja') && (
                <>
                  <div css={BottomSpacing}>
                    <Controller
                      control={control}
                      name='postureMode'
                      render={({ field: { onChange, value } }) => (
                        <FormControlLabel
                          label={
                            <span css={InlineHelp}>
                              {t('postureMode', '姿勢モード')}
                              <Tooltip
                                title={
                                  <span>
                                    {t(
                                      'pleaseTryThisModeIfYourPostureIsPreventingTheLevelOfUrinaryRetentionFromIncreasing',
                                      '姿勢の影響で尿のたまり具合があがらない場合にお試しください。',
                                    )}
                                  </span>
                                }
                                placement='top'
                                arrow
                              >
                                <HelpSharpIcon fontSize='small' sx={{ color: color.dark_gray }} />
                              </Tooltip>
                            </span>
                          }
                          control={
                            <Checkbox
                              sx={{ padding: 0, marginRight: '8px' }}
                              checked={value}
                              onChange={(v) => onChange(v.target.checked)}
                            />
                          }
                        />
                      )}
                    />
                  </div>
                </>
              )}
              <div css={ContingencyModeSub}>
                {t(
                  'rescueModeAndPostureModeMayMakeItEasierForTheLevelOfUrinaryRetentionToIncrease',
                  '救済モードと姿勢モードは尿のたまり具合の数値をあがりやすくします。',
                )}
                <br />
                {i18n.language.startsWith('ja') ? (
                  <>
                    ※ ご利用前に
                    <a href={SUPPORT_URL.howToWear.ja} target='_blank' rel='noreferrer'>
                      装着手順
                    </a>
                    を再度ご確認ください。
                  </>
                ) : (
                  <>
                    * Please also refer to{' '}
                    <a href={SUPPORT_URL.howToWear.en} target='_blank' rel='noreferrer'>
                      the placement instructions
                    </a>{' '}
                    before using.
                  </>
                )}
                <br />
                {t(
                  'theLevelOfUrinaryRetentionMayNotNecessarilyIncreaseAsAResultOfUsingTheseModes.',
                  '※必ず数値があがるわけではございません。',
                )}
                <br />
                {t(
                  'thereIsAPossibilityThatTheLevelOfUrinaryRetentionMayIncreaseSuddenlySoPleaseBeAwareWhenUsingTheseModes.',
                  '※数値が急激にあがる可能性もありますので、ご利用には注意ください',
                )}
                <br />
              </div>
            </div>
          </div>
          {i18n.language.startsWith('ja') && (
            <Accordion disableGutters sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
              <AccordionSummary css={BottomLine} expandIcon={<ExpandMoreIcon />} sx={{ px: 0, mb: 2 }}>
                <div>ケア方針 / DFree利用目的</div>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0 }}>
                <div css={FormItem}>
                  <label css={Label} />
                  <div css={FormContent}>
                    <Controller
                      control={control}
                      name='carePlanMemo'
                      render={({ field: { onChange, value } }) => (
                        <TextField
                          sx={{ width: '100%' }}
                          label='ケア方針 / DFree利用目的'
                          onChange={onChange}
                          value={value}
                        />
                      )}
                    />
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>
          )}
          <Accordion disableGutters sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
            <AccordionSummary css={BottomLine} expandIcon={<ExpandMoreIcon />} sx={{ px: 0, mb: 2 }}>
              <div>{t('assistToTheToiletAtASetTime', '排泄ケア時刻設定')}</div>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0 }}>
              <div css={FormItem}>
                <label css={[Label]} />
                <div css={FormContent}>
                  {careSchedule.map((timing, index) => (
                    <div key={index} css={CareTimingContainer}>
                      <div>{t('ProvideCare', '排泄ケア時刻')}</div>
                      <div css={CareScheduleContainer}>
                        <div css={ScheduleSelector}>
                          <Select
                            listData={selectableTimingList}
                            value={String(timing)}
                            fullWidth
                            isDisplayEmpty
                            isEmptyValue={timing === localHoursWithUnSelected[0].key}
                            onChange={(v) => {
                              onChangeCareSchedule('change', String(v.key), index);
                            }}
                          />
                        </div>
                        {timing !== '' && (
                          <IconButton
                            sx={{ borderRadius: '4px', border: `1px solid ${color.border_gray}` }}
                            onClick={() => onChangeCareSchedule('delete', undefined, index)}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                            <span css={Font14}>{t('delete', '削除')}</span>
                          </IconButton>
                        )}
                      </div>
                    </div>
                  ))}
                  <Button
                    sx={{ borderColor: `${color.green}` }}
                    variant='outlined'
                    disabled={!enableAddTimingButton}
                    onClick={() => onChangeCareSchedule('add')}
                    css={AddTimingButtonStyle}
                  >
                    + {t('AddScheduledTime', '排泄ケア時刻を追加')}
                  </Button>
                </div>
              </div>
            </AccordionDetails>
          </Accordion>
          <div css={BarTitle}>{t('dFreeBenSettings', 'DFreeBen設定')}</div>
          <div css={FormItem}>
            <label css={[Label, TopSpacing]}>DFree Ben SN</label>
            <div css={FormContent}>
              <FormControl sx={{ width: '100%' }}>
                <Select
                  style={{ width: '100%', marginBottom: '8px' }}
                  label='DFree Ben SN'
                  listData={memoBDevices}
                  value={bDeviceId}
                  onChange={(v) => onChangeBDeviceId(v.key as string)}
                />
              </FormControl>
            </div>
          </div>
          <div css={[BottomLine, TopSpacing, InnerBottomSpacing, BottomSpacing]}>通知</div>
          <div css={FormItem}>
            <label css={Label} />
            <div css={FormContent}>
              <Controller
                control={control}
                name='gasPoopedNotification'
                render={({ field: { onChange, value } }) => (
                  <div>
                    <FormControlLabel
                      sx={{ marginBottom: '8px' }}
                      label={t('receiveOdorDetectionNotifications', '臭い検知通知を受け取る')}
                      control={
                        <>
                          <Checkbox
                            sx={{ padding: 0, marginRight: '8px' }}
                            checked={value ?? false}
                            onChange={(v) => onChange(v.target.checked)}
                          />
                          <img css={NotificationImage} src={`/images/notification/icon_pooped.svg`} />
                        </>
                      }
                    />
                  </div>
                )}
              />
              <div css={CheckboxSelectContainer}>
                <Controller
                  control={control}
                  name='gasStrength'
                  render={({ field: { onChange, value } }) => (
                    <>
                      <div css={FlexBetween}>
                        <div>{t('odorStrength', '臭いの強さ（推奨）')}</div>
                        <Select listData={GAS_STRENGTH} value={String(value)} onChange={(v) => onChange(v.key)} />
                      </div>
                      <div css={ContingencyModeSub}>
                        {t(
                          'ifYouLowerTheSettingItWillBeMoreLikelyToDetectFaintOdorsAndWillBeMoreLikelyToSoundNotifications',
                          '値を小さくすると、弱い臭いでも反応し、通知が鳴りやすくなります。',
                        )}
                      </div>
                    </>
                  )}
                />
                <Controller
                  control={control}
                  name='gasDuration'
                  render={({ field: { onChange, value } }) => (
                    <>
                      <div css={FlexBetween}>
                        <div>{t('odorDuration', '臭いの持続時間')}</div>
                        <Select listData={GAS_DURATION} value={String(value)} onChange={(v) => onChange(v.key)} />
                      </div>
                      <div css={ContingencyModeSub}>
                        {t(
                          'ifYouSetItShorterItWillBeMoreLikelyToDetectEvenShortTermOdorsAndWillBeMoreLikelyToSoundANotification',
                          '値を小さくすると、短時間の臭いでも反応し、通知が鳴りやすくなります。',
                        )}
                      </div>
                    </>
                  )}
                />
              </div>
              <Controller
                control={control}
                name='gasPredictionNotification'
                render={({ field: { onChange, value } }) => (
                  <div>
                    <FormControlLabel
                      sx={{ marginBottom: '8px' }}
                      label={t('receiveNotificationOfPredictedDefecationTime', '排便予測時間通知を受け取る')}
                      control={
                        <>
                          <Checkbox
                            sx={{ padding: 0, marginRight: '8px' }}
                            checked={value ?? false}
                            onChange={(v) => onChange(v.target.checked)}
                          />
                          <img css={NotificationImage} src={`/images/notification/icon_prediction.svg`} />
                        </>
                      }
                    />
                  </div>
                )}
              />
            </div>
          </div>
        </div>
      </ThemeProvider>
    );
  },
);
